import React, { useEffect, useMemo, useState } from 'react';
import apiClient, { proxyImageUrl } from '../api/client';
import { useAuthStore } from '../store';
import '../styles/product-card.css';

const _bustUrl = (url) => {
  if (!url) return url;
  try {
    const raw = localStorage.getItem('alldrop_photoTs');
    if (!raw) return url;
    const base = url.split('?')[0];
    const ts = JSON.parse(raw)[base];
    return ts ? `${base}?t=${ts}` : url;
  } catch { return url; }
};

const ProductCard = ({ product, onSelect, onAlert, hasAlert }) => {
  const currentUser = useAuthStore((state) => state.user);
  const [imgError, setImgError] = useState(false);
  const [bustVersion, setBustVersion] = useState(0);
  useEffect(() => {
    const handler = () => setBustVersion(v => v + 1);
    window.addEventListener('photo-rotated', handler);
    return () => window.removeEventListener('photo-rotated', handler);
  }, []);
  const photoSrc = useMemo(() => _bustUrl(product.photo_url), [product.photo_url, bustVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const [skuCopied, setSkuCopied] = useState(false);
  const [photoCopyState, setPhotoCopyState] = useState('idle');

  const handleSkuClick = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(product.sku).then(() => {
      setSkuCopied(true);
      setTimeout(() => setSkuCopied(false), 600);
    });
  };

  const handlePhotoCopy = (e) => {
    e.stopPropagation();
    if (!product.photo_url || photoCopyState === 'loading') return;

    const buildPng = async () => {
      const url = product.photo_url;
      const blob = url.includes('alldrop.ink/images/')
        ? await fetch(url).then(r => r.blob())
        : await apiClient.get(`/products/proxy-image?url=${encodeURIComponent(url)}`, { responseType: 'blob' }).then(r => r.data);
      const bmp = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = bmp.width;
      canvas.height = bmp.height;
      canvas.getContext('2d').drawImage(bmp, 0, 0);
      return new Promise((resolve, reject) =>
        canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png')
      );
    };

    setPhotoCopyState('loading');
    navigator.clipboard.write([new ClipboardItem({ 'image/png': buildPng() })]).then(() => {
      setPhotoCopyState('done');
      setTimeout(() => setPhotoCopyState('idle'), 1000);
    }).catch(err => {
      console.error('Photo copy failed:', err);
      setPhotoCopyState('idle');
    });
  };

  const sizes = product.sizes ? product.sizes.split(' ').filter(Boolean) : [];

  return (
    <div className="product-card" onClick={() => onSelect(product)}>
      <div className="product-image">
        {product.photo_url && !imgError ? (
          <img
            src={photoSrc}
            alt={product.name}
            className="product-photo"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="image-placeholder">👟</div>
        )}

        <span className="sku-badge" onClick={handleSkuClick} title="Копировать SKU">
          {skuCopied ? '✓ Скопировано' : `${product.season?.includes('Зима') ? '❄️ ' : ''}${product.sku}`}
        </span>

        {product.photo_url && (
          <button className="photo-copy-btn" onClick={handlePhotoCopy} title="Копировать фото">
            {photoCopyState === 'loading'
              ? <span className="photo-copy-spinner" />
              : photoCopyState === 'done'
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>}
          </button>
        )}

        {currentUser && onAlert && (
          <button
            className={`bell-btn${hasAlert ? ' bell-btn--active' : ''}`}
            onClick={(e) => { e.stopPropagation(); onAlert(); }}
            title={hasAlert ? 'Управление подпиской' : 'Сообщить о поступлении'}
            style={{ position: 'absolute', bottom: 6, right: 6 }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
            </svg>
          </button>
        )}
      </div>

      <div className="product-info">
        {sizes.length > 0 && (
          <div className="sizes-section">
            <div className="sizes-grid">
              {sizes.map((size) => (
                <button
                  key={size}
                  className="size-btn"
                  onClick={(e) => { e.stopPropagation(); onSelect({ ...product, selectedSize: size }); }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        )}
        {'availability' in product && (() => {
          const text = product.availability || 'Нет в наличии';
          const isIn = product.availability && !product.availability.toLowerCase().includes('нет');
          return (
            <div className={`product-availability${isIn ? ' product-availability--in' : ' product-availability--out'}`}>
              {text}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ProductCard;
